import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../styles/theme';
import { 
  FaChartLine, 
  FaBatteryFull, 
  FaTint, 
  FaMapMarkedAlt, 
  FaShieldAlt,
  FaMobile,
  FaCloud,
  FaBell,
  FaUsers,
  FaCogs
} from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing['4xl']} ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['5xl']};
  margin-bottom: ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.primary.main}, ${theme.colors.secondary.main});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const PoweredBy = styled.div`
  margin-top: ${theme.spacing['2xl']};
  padding: ${theme.spacing.lg};
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius.xl};
  backdrop-filter: blur(10px);
`;

const PoweredByText = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
  margin: 0;
`;

const PoweredByLink = styled.a`
  color: ${theme.colors.primary.light};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.semibold};
  
  &:hover {
    color: ${theme.colors.primary.main};
    text-decoration: underline;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing['3xl']};
`;

const FeatureCard = styled(motion.div)`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing['2xl']};
  backdrop-filter: blur(10px);
  transition: ${theme.transitions.default};
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${theme.colors.primary.main};
    box-shadow: ${theme.shadows.xl};
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.lg};
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.text.primary};
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  font-size: ${theme.typography.fontSize.base};
`;

const Section = styled.div`
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize['4xl']};
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
  color: ${theme.colors.text.primary};
`;

const TechStack = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing['2xl']};
`;

const TechItem = styled.div`
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid rgba(59, 130, 246, 0.1);
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.lg};
  text-align: center;
`;

const TechName = styled.h4`
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.sm};
`;

const TechDescription = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const Features = () => {
  const coreFeatures = [
    {
      icon: <FaChartLine />,
      title: "Real-Time Monitoring",
      description: "Live data streaming from SignalK servers with instant updates on vessel status, navigation, and environmental conditions."
    },
    {
      icon: <FaBatteryFull />,
      title: "Electrical System Management",
      description: "Comprehensive battery monitoring, solar panel tracking, and electrical system diagnostics with predictive maintenance alerts."
    },
    {
      icon: <FaTint />,
      title: "Fluid Management",
      description: "Monitor fuel levels, water tanks, and bilge pumps with automated alerts and consumption tracking for optimal efficiency."
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Navigation & Tracking",
      description: "Advanced GPS tracking, route planning, and location sharing with real-time position updates and trip logging."
    },
    {
      icon: <FaShieldAlt />,
      title: "Safety & Security",
      description: "Comprehensive security monitoring with intrusion detection, anchor watch, and emergency alert systems."
    },
    {
      icon: <FaMobile />,
      title: "Mobile-First Design",
      description: "Responsive interface optimized for tablets and phones with critical monitoring accessible from anywhere on board."
    }
  ];

  const advancedFeatures = [
    {
      icon: <FaCloud />,
      title: "Cloud Integration",
      description: "Seamless data synchronization across devices with secure cloud backup and remote access capabilities."
    },
    {
      icon: <FaBell />,
      title: "Smart Alerts",
      description: "Intelligent notification system with customizable alerts for critical events and predictive maintenance warnings."
    },
    {
      icon: <FaUsers />,
      title: "Multi-User Support",
      description: "Team collaboration features with role-based access control and shared dashboard configurations."
    },
    {
      icon: <FaCogs />,
      title: "API Integration",
      description: "Open API for third-party integrations and custom applications with comprehensive documentation."
    }
  ];

  const techStack = [
    { name: "React", description: "Modern frontend framework" },
    { name: "Node.js", description: "Robust backend runtime" },
    { name: "PostgreSQL", description: "Reliable data storage" },
    { name: "SignalK", description: "Marine data standard" },
    { name: "WebSocket", description: "Real-time communication" },
    { name: "Docker", description: "Containerized deployment" }
  ];

  return (
    <Container>
      <Header>
        <Title>Powerful Marine Technology</Title>
        <Subtitle>
          Built for modern boaters who demand reliability, performance, and peace of mind on the water.
        </Subtitle>
        <PoweredBy>
          <PoweredByText>
            Powered by <PoweredByLink href="https://www.modernmarine.ca" target="_blank" rel="noopener noreferrer">Modern Marine</PoweredByLink> - 
            Your trusted partner in marine technology solutions
          </PoweredByText>
        </PoweredBy>
      </Header>

      <Section>
        <SectionTitle>Core Features</SectionTitle>
        <FeaturesGrid>
          {coreFeatures.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Section>

      <Section>
        <SectionTitle>Advanced Capabilities</SectionTitle>
        <FeaturesGrid>
          {advancedFeatures.map((feature, index) => (
            <FeatureCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Section>

      <Section>
        <SectionTitle>Technology Stack</SectionTitle>
        <TechStack>
          {techStack.map((tech, index) => (
            <TechItem
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <TechName>{tech.name}</TechName>
              <TechDescription>{tech.description}</TechDescription>
            </TechItem>
          ))}
        </TechStack>
      </Section>
    </Container>
  );
};

export default Features;
